// #1: Menu Upload History — every menu Excel/CSV import with outcome + the
// original file (download / re-upload) + delete. Read-only over the log table.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { toast } from 'react-toastify';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const user = JSON.parse(sessionStorage.getItem('user') || '{}');
const token = sessionStorage.getItem('token');
const auth = { Authorization: `Bearer ${token}` };

export default function MenuUploadHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    if (!company?.id) return;
    setLoading(true);
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    fetch(
      `${config.API_BASE_URL}/menuupload/history/${company.id}?${qs.toString()}`,
      { headers: auth },
    )
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (row) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/menuupload/file/${row.id}`, {
        headers: auth,
      });
      const d = await res.json();
      if (!d?.fileBase64) {
        toast.info('Original file not stored for this record.');
        return;
      }
      const bin = atob(d.fileBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes]));
      const a = document.createElement('a');
      a.href = url;
      a.download = d.fileName || `menu-import-${row.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  const toggleActive = async (row) => {
    const next = !row.isActive;
    const verb = next ? 'Activate' : 'Deactivate';
    if (
      !window.confirm(
        `${verb} "${row.fileName}"?\n${next ? 'Its' : 'All'} ${row.linkedItems || 0} linked item(s) will ${next ? 'reappear in' : 'be hidden from'} POS / QR / ordering screens.`,
      )
    )
      return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/menuupload/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          Id: row.id,
          Active: next,
          UserId: user?.id || user?.Id || 0,
        }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`${verb}d — ${d.affectedItems} item(s) updated`);
      setRows((rs) =>
        rs.map((x) => (x.id === row.id ? { ...x, isActive: next } : x)),
      );
    } catch {
      toast.error(`Failed to ${verb.toLowerCase()}`);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this upload record (${row.fileName})?`)) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/menuupload/${row.id}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok) throw new Error();
      toast.success('Record deleted');
      setRows((r) => r.filter((x) => x.id !== row.id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.fileName || '').toLowerCase().includes(q) ||
      (r.uploadedByName || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f6fb', minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          color: '#fff',
          background: 'linear-gradient(120deg,#0a2540,#1d4ed8)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
        <HistoryIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant='h5' fontWeight={800}>
            Menu Upload History
          </Typography>
          <Typography sx={{ opacity: 0.85 }}>
            Every menu import — outcome, original file & audit
          </Typography>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          p: 1.5,
          mb: 2,
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          border: '1px solid #e8eef7',
        }}>
        <TextField type='date' size='small' label='From' InputLabelProps={{ shrink: true }}
          value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField type='date' size='small' label='To' InputLabelProps={{ shrink: true }}
          value={to} onChange={(e) => setTo(e.target.value)} />
        <TextField size='small' placeholder='Search file / user' value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} />
        <Button variant='contained' startIcon={<RefreshIcon />} onClick={load}
          sx={{ borderRadius: 2, fontWeight: 700 }}>
          Apply
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e8eef7' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size='small' sx={{ '& .MuiTableCell-body': { color: '#1e293b' } }}>
            <TableHead>
              <TableRow>
                {['File', 'Uploaded', 'By', 'Categories', 'Items', 'Success', 'Failed', 'Duration', 'Status', 'Reason', ''].map(
                  (h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0a2540', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align='center' sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align='center' sx={{ py: 5, color: '#94a3b8' }}>
                    No uploads found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.fileName || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.uploadedAt ? new Date(r.uploadedAt).toLocaleString('en-IN') : '—'}
                    </TableCell>
                    <TableCell>{r.uploadedByName || '—'}</TableCell>
                    <TableCell align='center'>{r.totalCategories}</TableCell>
                    <TableCell align='center'>{r.totalItems}</TableCell>
                    <TableCell align='center'>
                      <Chip size='small' color='success' label={r.successCount} />
                    </TableCell>
                    <TableCell align='center'>
                      {r.failedCount > 0 ? (
                        <Chip size='small' color='error' label={r.failedCount} />
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                      {r.durationMs ? `${(r.durationMs / 1000).toFixed(1)}s` : '—'}
                    </TableCell>
                    <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        size='small'
                        color={r.isActive ? 'success' : 'default'}
                        variant={r.isActive ? 'filled' : 'outlined'}
                        label={r.isActive ? 'Active' : 'Inactive'}
                      />
                      {r.linkedItems > 0 && (
                        <Typography variant='caption' sx={{ display: 'block', color: '#64748b' }}>
                          {r.linkedItems} item(s)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, fontSize: 12, color: '#b45309' }}>
                      {r.failureReason || ''}
                    </TableCell>
                    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={r.isActive ? 'Deactivate menu (hide items)' : 'Activate menu (show items)'}>
                        <span>
                          <IconButton
                            size='small'
                            color={r.isActive ? 'success' : 'default'}
                            disabled={!r.linkedItems}
                            onClick={() => toggleActive(r)}>
                            {r.isActive ? (
                              <ToggleOnIcon fontSize='small' />
                            ) : (
                              <ToggleOffIcon fontSize='small' />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Download / re-upload'>
                        <span>
                          <IconButton size='small' disabled={!r.hasFile} onClick={() => download(r)}>
                            <DownloadIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Delete record'>
                        <IconButton size='small' color='error' onClick={() => remove(r)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
